/**
 * The camera input source. Deliberately thin: everything that can be decided
 * without a camera lives in landmarks.ts and the mapping layer, so this file
 * holds only the parts that genuinely need a browser — getUserMedia, the
 * HandLandmarker, and the rAF loop.
 *
 * iOS notes, learned in the Phase 0 spike and non-negotiable here:
 *
 *  - The <video> MUST carry `playsinline` and `muted`. Without them Safari
 *    refuses to play inline and renders a crossed-out play button instead.
 *  - play() must be called inside the user gesture that started audio.
 *  - Safari only offers the camera permission prompt over HTTPS. Over plain
 *    HTTP it fails with a bare NotAllowedError that reads like a denial.
 */

import { FilesetResolver, HandLandmarker, type HandLandmarkerResult } from '@mediapipe/tasks-vision';
import type { FeatureFrame, Vec3 } from '../mapping/types';
import type { FrameCallback, InputSource } from '../input/source';
import { DEFAULT_RANGES, type Ranges } from '../mapping/features';
import { frameFromHands, type RawHand } from './landmarks';

/**
 * The wasm fileset is served from this origin, vendored out of the installed
 * @mediapipe/tasks-vision package by scripts/copy-wasm.mjs. It must be the same
 * release as the bundled JS API; copying it from node_modules is what makes
 * that guaranteed rather than hand-maintained.
 *
 * Relative, like the scratch worklet, because vite is configured with
 * `base: './'` and the app has to work from a subpath.
 */
const WASM_BASE = 'vision/wasm';

/**
 * The ~7.5 MB hand model. Served from this origin when it has been vendored
 * (scripts/copy-wasm.mjs), and from Google's model host otherwise, so a deploy
 * that skipped the vendor step still works instead of failing at the gate.
 */
const LOCAL_MODEL = 'vision/hand_landmarker.task';
const REMOTE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

/**
 * Every await in the start path is bounded.
 *
 * This is not defensive padding. The first deploy hung forever on "Loading the
 * hand model…" because nothing here had a timeout: a promise that never settles
 * is not caught by try/catch, so the GPU→CPU fallback below could never fire,
 * and the user got a dead screen with no error and no way back.
 */
function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: number;
  const bell = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => reject(new Error(`${what} timed out after ${Math.round(ms / 1000)}s`)), ms);
  });
  return Promise.race([p, bell]).finally(() => window.clearTimeout(timer)) as Promise<T>;
}

/** One cheap HEAD probe, bounded — a hanging probe used to hang the whole app. */
async function resolveModelUrl(): Promise<string> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 4000);
  try {
    const res = await fetch(LOCAL_MODEL, { method: 'HEAD', signal: ctrl.signal });
    if (res.ok) return LOCAL_MODEL;
  } catch { /* not vendored, blocked, or too slow — use the remote host */ }
  finally { window.clearTimeout(t); }
  return REMOTE_MODEL;
}

/**
 * Fetch the model ourselves rather than handing MediaPipe a URL.
 *
 * Two reasons, both learned the hard way. It makes the ~7.5 MB download
 * REPORTABLE — inside MediaPipe it is opaque, so a slow cellular fetch and a
 * dead hang look identical. And it lets the download be bounded and retried
 * against the remote host without going through the landmarker at all.
 */
async function fetchModel(url: string, onProgress: (msg: string) => void): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`model fetch failed: HTTP ${res.status} for ${url}`);

  const total = Number(res.headers.get('content-length')) || 0;
  const mb = (n: number): string => (n / 1048576).toFixed(1);

  // No streaming body (or no length): fall back to a plain buffer read.
  if (!res.body) return new Uint8Array(await res.arrayBuffer());

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.length;
    onProgress(total
      ? `Downloading the hand model — ${mb(got)} / ${mb(total)} MB`
      : `Downloading the hand model — ${mb(got)} MB`);
  }
  const out = new Uint8Array(got);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

export interface CameraOptions {
  maxHands?: number;
  /** Flip role assignment for a rear or already-mirrored camera. */
  swapRoles?: boolean;
  width?: number;
  height?: number;
  fps?: number;
  onStatus?: (msg: string) => void;
}

export interface CameraSource extends InputSource {
  readonly video: HTMLVideoElement;
  /**
   * Apply calibrated ranges. Camera-path only — the pad's pointer is already
   * 0..1 and fixture replay stays on defaults so its determinism is portable.
   */
  setRanges(r: Ranges): void;
  /** GPU or CPU, whichever the delegate fallback settled on. */
  readonly delegate: string;
  readonly inferenceMs: number;
  setMaxHands(n: number): Promise<void>;
  latestHands(): readonly RawHand[];
}

/** MediaPipe's result → the plain shapes landmarks.ts understands. */
function unwrap(res: HandLandmarkerResult): RawHand[] {
  const out: RawHand[] = [];
  const lists = res.landmarks ?? [];
  for (let i = 0; i < lists.length; i++) {
    const hd = res.handednesses?.[i]?.[0];
    out.push({
      points: lists[i].map((p) => [p.x, p.y, p.z] as Vec3),
      label: hd?.categoryName === 'Left' ? 'Left' : 'Right',
      score: hd?.score ?? 0,
    });
  }
  return out;
}

export function createCameraSource(opts: CameraOptions = {}): CameraSource {
  const {
    maxHands: initialHands = 2, swapRoles = false,
    width = 640, height = 480, fps = 30, onStatus,
  } = opts;

  const video = document.createElement('video');
  // These are load-bearing on iOS — see the header note. The element must also
  // end up IN the document; src/ui/preview.ts mounts it, because a detached
  // video decodes on desktop and yields nothing on a phone.
  //
  // No `autoplay`: play() is called explicitly below, and the autoplay
  // attribute is what opts an element into WebKit's off-screen pause policy.
  video.playsInline = true;
  video.muted = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', 'true'); // older iOS
  video.setAttribute('muted', '');

  let landmarker: HandLandmarker | null = null;
  let stream: MediaStream | null = null;
  let delegate = 'CPU';
  let maxHands = initialHands;
  let raf = 0;
  let cb: FrameCallback | null = null;
  let lastMs = 0;
  let lastVideoTime = -1;
  let inferenceMs = 0;
  let hands: RawHand[] = [];
  let ranges: Ranges = DEFAULT_RANGES;
  let disposed = false;
  let stalledSince = 0;
  let stalled = false;

  /**
   * WebKit pauses a muted video that leaves the viewport or when the page is
   * backgrounded, and it does not resume by itself. Guarded three ways:
   * dispose() nulls srcObject which itself fires `pause`; re-playing while
   * hidden just ping-pongs against the interruption; and once the track has
   * ENDED only a fresh getUserMedia recovers, so say so instead of retrying.
   */
  const onPause = (): void => {
    if (disposed || document.visibilityState !== 'visible') return;
    if (stream?.getVideoTracks()[0]?.readyState === 'ended') {
      onStatus?.('The camera was released by the system — reload to restart it.');
      return;
    }
    void video.play().catch(() => { /* a later visibilitychange may recover it */ });
  };
  video.addEventListener('pause', onPause);

  const onVisible = (): void => {
    if (disposed || document.visibilityState !== 'visible') return;
    if (stream?.getVideoTracks()[0]?.readyState === 'ended') {
      onStatus?.('The camera was released while the app was in the background — reload to restart it.');
      return;
    }
    if (video.paused) void video.play().catch(() => { /* reported on the next pause */ });
  };
  document.addEventListener('visibilitychange', onVisible);

  async function initVision(): Promise<void> {
    onStatus?.('Loading the tracking runtime…');
    const fileset = await withTimeout(
      FilesetResolver.forVisionTasks(WASM_BASE), 30_000, 'Loading the tracking runtime',
    );

    onStatus?.('Locating the hand model…');
    const modelUrl = await resolveModelUrl();

    // Bounded generously: this is the ~7.5 MB download, and cellular is slow —
    // but not unbounded, because "slow" and "never" must be distinguishable.
    let modelBuffer: Uint8Array;
    try {
      modelBuffer = await withTimeout(
        fetchModel(modelUrl, (m) => onStatus?.(m)), 120_000, 'Downloading the hand model',
      );
    } catch (err) {
      // A vendored copy that 404s or stalls should not be fatal while Google's
      // host is still reachable.
      if (modelUrl === LOCAL_MODEL) {
        onStatus?.('Local model unavailable — retrying from the model host…');
        modelBuffer = await withTimeout(
          fetchModel(REMOTE_MODEL, (m) => onStatus?.(m)), 120_000, 'Downloading the hand model',
        );
      } else {
        throw err;
      }
    }

    const options = (d: 'GPU' | 'CPU') => ({
      // A fresh copy per attempt: createFromOptions consumes the buffer, so the
      // CPU retry would otherwise be handed a detached one.
      baseOptions: { modelAssetBuffer: modelBuffer.slice(), delegate: d },
      runningMode: 'VIDEO' as const,
      numHands: maxHands,
      minHandDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    // GPU first, CPU fallback. The timeout is the point: on some iOS devices the
    // GPU delegate never settles rather than rejecting, and an untimed await
    // there is exactly what hung the first deploy.
    onStatus?.('Starting the tracker (GPU)…');
    try {
      landmarker = await withTimeout(
        HandLandmarker.createFromOptions(fileset, options('GPU')), 20_000, 'GPU tracker startup',
      );
      delegate = 'GPU';
      return;
    } catch (err) {
      onStatus?.(`GPU tracking unavailable (${(err as Error).message}) — falling back to CPU…`);
    }

    landmarker = await withTimeout(
      HandLandmarker.createFromOptions(fileset, options('CPU')), 45_000, 'CPU tracker startup',
    );
    delegate = 'CPU';
  }

  function loop(): void {
    raf = requestAnimationFrame(loop);
    if (!landmarker || video.readyState < 2) return;

    // detectForVideo rejects a repeated timestamp; skip frames the camera has
    // not actually advanced past. This gate is correct and is what the spike
    // does — but on its own it turns a stalled stream into a silent freeze.
    if (video.currentTime === lastVideoTime) {
      const t = performance.now();
      if (!stalledSince) stalledSince = t;
      else if (!stalled && t - stalledSince > 1000) {
        stalled = true;
        // Do NOT re-run detection on the frozen frame: it would re-emit the
        // same landmarks and a pinch held at the moment of the stall would
        // stay held forever. Emit one empty frame — the vocabulary sees the
        // hand vanish and releases everything held, exactly as a dropout.
        onStatus?.('Camera stalled — no new frames. Scroll the preview back into view, or reload.');
        cb?.(frameFromHands([], 1 / fps, swapRoles, maxHands, ranges));
      }
      return;
    }
    stalledSince = 0;
    if (stalled) { stalled = false; onStatus?.(`Tracking on ${delegate}.`); }
    lastVideoTime = video.currentTime;

    const now = performance.now();
    const dt = lastMs ? Math.min(0.1, Math.max(0.001, (now - lastMs) / 1000)) : 1 / fps;
    lastMs = now;

    let result: HandLandmarkerResult;
    try {
      const t0 = performance.now();
      result = landmarker.detectForVideo(video, now);
      inferenceMs = performance.now() - t0;
    } catch {
      return; // a dropped frame is not a failure worth tearing the loop down for
    }

    hands = unwrap(result);
    // The camera emits measures only. Pinch detection — with its hysteresis
    // and its dropout release — happens once, in the control pipeline, so the
    // camera and the pad cannot disagree about what "engaged" means.
    const frame: FeatureFrame = frameFromHands(hands, dt, swapRoles, maxHands, ranges);
    cb?.(frame);
  }

  return {
    id: 'camera',
    root: null, // the preview owns presentation; see src/ui/preview.ts
    video,
    get delegate() { return delegate; },
    get inferenceMs() { return inferenceMs; },
    latestHands: () => hands,
    setRanges(r) { ranges = r; },

    onFrame(fn) { cb = fn; },

    async start() {
      // Already initialized — a stop()/start() cycle (the input toggle) must
      // resume the loop, not re-run getUserMedia: a second permission hop can
      // fail outside a user gesture, and the stream never went away.
      if (stream) {
        lastMs = 0;
        lastVideoTime = -1;
        if (video.paused) await video.play();
        if (!raf) raf = requestAnimationFrame(loop);
        return;
      }
      onStatus?.('Requesting the camera…');
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: width }, height: { ideal: height }, frameRate: { ideal: fps },
        },
      });
      video.srcObject = stream;
      // Must be in the document BEFORE play(): iOS will not decode a detached
      // element, and start() runs before the preview exists. A holding spot in
      // the body keeps it rendered until preview.attach() re-parents it.
      if (!video.isConnected) {
        video.classList.add('camera-video-parked');
        document.body.appendChild(video);
      }
      // Bounded but NOT fatal: a play() promise that never settles must not
      // block startup, and the readyState gate in loop() already copes with a
      // stream that has not produced a frame yet.
      await withTimeout(video.play(), 8000, 'Camera playback start')
        .catch(() => onStatus?.('Camera is slow to start — continuing.'));
      await initVision();
      onStatus?.(`Tracking on ${delegate}.`);
      lastMs = 0;
      lastVideoTime = -1;
      if (!raf) raf = requestAnimationFrame(loop);
    },

    stop() {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
    },

    videoTracks() { return stream ? stream.getVideoTracks() : []; },

    async setMaxHands(n) {
      maxHands = n;
      try { await landmarker?.setOptions({ numHands: n }); } catch { /* keep the old count */ }
    },

    dispose() {
      // Set first: nulling srcObject runs the load algorithm and fires `pause`,
      // and the handler must not try to restart a torn-down element.
      disposed = true;
      video.removeEventListener('pause', onPause);
      document.removeEventListener('visibilitychange', onVisible);
      this.stop();
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      video.srcObject = null;
      landmarker?.close();
      landmarker = null;
    },
  };
}
